import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  useApprenticeAction,
  useApprenticeGoalTemplates,
  useApprenticeship,
  useApprenticeships,
  useProjects,
  useUsers
} from '../api/queries'
import type { ApprenticeGoal, Apprenticeship } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { useCreateRequest } from '../lib/createRequest'
import { formatDate, formatDateTime } from '../lib/format'
import { IntranetPhotoThumb } from './IntranetBits'
import { Card, EmptyState, ErrorBox, Loading } from './ui'

/**
 * Lærlingeplanen (docs/DECISIONS.md beslutning 130): ansvarlig svend, praktikmål og logbog. Skoleperioderne er
 * fravær af typen "Skole" - så de også står i kalenderen.
 *
 * Planen ses af lærlingen, den ansvarlige svend og kontoret. Kun svenden eller kontoret krydser mål af.
 */

const errorText = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback)

function todayIso() {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// ---------------------------------------------------------------------------
// Fanen
// ---------------------------------------------------------------------------

export function ApprenticesTab() {
  const { isIntranetAdmin } = useAuth()
  const { data, isLoading, error } = useApprenticeships()
  const [creating, setCreating] = useState(false)

  useCreateRequest(['laerling'], () => {
    if (isIntranetAdmin) setCreating(true)
  })

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  const active = (data ?? []).filter((a) => !a.completedAt)
  const done = (data ?? []).filter((a) => a.completedAt)

  return (
    <div className="stack" style={{ gap: 16 }}>
      {isIntranetAdmin && !creating && (
        <div>
          <button className="btn btn--primary" onClick={() => setCreating(true)}>
            🎓 Ny lærlingeplan
          </button>
        </div>
      )}
      {creating && <PlanForm onClose={() => setCreating(false)} />}

      <Card title="Lærlinge">
        {active.length === 0 ? (
          <EmptyState>Der er ingen lærlingeplaner i gang.</EmptyState>
        ) : (
          <div className="stack" style={{ gap: 8 }}>
            {active.map((a) => (
              <Link
                key={a.id}
                to={`/intranet/laerling/${a.id}`}
                className="row row--between"
                style={{ gap: 10, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border, #E2E8F0)' }}
              >
                <div style={{ minWidth: 0 }}>
                  <div className="small">
                    <strong>{a.apprentice.displayName}</strong> · {a.education}
                  </div>
                  <div className="tiny muted">
                    Svend: {a.journeyman?.displayName ?? 'ikke sat'} · startet {formatDate(a.startDate)}
                    {a.lastLogDate ? ` · logbog ${formatDate(a.lastLogDate)}` : ' · ingen logbog endnu'}
                  </div>
                </div>
                <span className="tiny" style={{ whiteSpace: 'nowrap' }}>
                  {a.goalsApproved}/{a.goalsTotal} mål
                </span>
              </Link>
            ))}
          </div>
        )}
      </Card>

      {isIntranetAdmin && <TemplatesCard />}

      {done.length > 0 && (
        <Card title="Afsluttede">
          {done.map((a) => (
            <Link key={a.id} to={`/intranet/laerling/${a.id}`} className="small" style={{ display: 'block', padding: '4px 0' }}>
              {a.apprentice.displayName} · afsluttet {formatDate(a.completedAt)}
            </Link>
          ))}
        </Card>
      )}
    </div>
  )
}

function PlanForm({ plan, onClose }: { plan?: Apprenticeship; onClose: () => void }) {
  const navigate = useNavigate()
  const action = useApprenticeAction()
  const { data: users } = useUsers()
  const [apprentice, setApprentice] = useState(plan ? String(plan.apprentice.id) : '')
  const [journeyman, setJourneyman] = useState(plan?.journeyman ? String(plan.journeyman.id) : '')
  const [education, setEducation] = useState(plan?.education ?? 'Elektriker')
  const [start, setStart] = useState(plan?.startDate ?? todayIso())
  const [end, setEnd] = useState(plan?.expectedEndDate ?? '')
  const [notes, setNotes] = useState(plan?.notes ?? '')
  const [error, setError] = useState<string | null>(null)
  const people = (users ?? []).filter((u) => u.isActive && !u.isHidden)
  // Laerlinge foerst i listen - men alle kan vaelges.
  const apprentices = [...people].sort((a, b) => Number(b.roles.includes('Lærling')) - Number(a.roles.includes('Lærling')))

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    const body = {
      apprenticeUserId: Number(apprentice),
      journeymanUserId: journeyman ? Number(journeyman) : null,
      education: education.trim() || null,
      startDate: start,
      expectedEndDate: end || null,
      notes: notes.trim() || null
    }
    try {
      const saved = await action.mutateAsync(plan ? { path: `/${plan.id}`, method: 'put', body } : { path: '', body })
      onClose()
      if (!plan) navigate(`/intranet/laerling/${saved.id}`)
    } catch (err) {
      setError(errorText(err, 'Planen kunne ikke gemmes.'))
    }
  }

  return (
    <Card title={plan ? 'Ret planen' : 'Ny lærlingeplan'}>
      <form className="stack" style={{ gap: 10 }} onSubmit={(e) => void submit(e)}>
        <label className="small">
          Lærling
          <select value={apprentice} onChange={(e) => setApprentice(e.target.value)} disabled={!!plan} required>
            <option value="">Vælg ...</option>
            {apprentices.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
                {u.roles.includes('Lærling') ? ' (lærling)' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="small">
          Ansvarlig svend
          <select value={journeyman} onChange={(e) => setJourneyman(e.target.value)}>
            <option value="">Ingen endnu - så er det kontoret</option>
            {people.filter((u) => String(u.id) !== apprentice).map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </select>
        </label>
        <label className="small">
          Uddannelse
          <input value={education} onChange={(e) => setEducation(e.target.value)} maxLength={100} />
        </label>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <label className="small">
            Start
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} required />
          </label>
          <label className="small">
            Forventet svendeprøve
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </label>
        </div>
        <label className="small">
          Noter
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={1000} />
        </label>
        {!plan && (
          <div className="tiny muted">
            Praktikmålene kopieres ind fra husets liste. Skoleperioderne skriver du som fravær "Skole" i kalenderen.
          </div>
        )}
        {error && <div className="error-box">{error}</div>}
        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn--primary" disabled={action.isPending}>
            {action.isPending ? 'Gemmer ...' : plan ? 'Gem' : 'Opret planen'}
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Fortryd
          </button>
        </div>
      </form>
    </Card>
  )
}

/** Husets liste over praktikmål. Kun nye planer får en rettelse. */
function TemplatesCard() {
  const [open, setOpen] = useState(false)
  const { data, isLoading } = useApprenticeGoalTemplates(open)
  const action = useApprenticeAction()
  const [draft, setDraft] = useState<{ key: string; id?: number; area: string; text: string; isActive: boolean }[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const rows = draft ?? (data ?? []).map((t) => ({ key: `t${t.id}`, id: t.id, area: t.area, text: t.text, isActive: t.isActive }))

  function change(key: string, patch: Partial<{ area: string; text: string; isActive: boolean }>) {
    setDraft(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  }

  async function save() {
    setError(null)
    try {
      await action.mutateAsync({
        path: '/goal-templates',
        method: 'put',
        body: { templates: rows.filter((r) => r.text.trim()).map((r) => ({ id: r.id, area: r.area.trim(), text: r.text.trim(), isActive: r.isActive })) }
      })
      setDraft(null)
    } catch (err) {
      setError(errorText(err, 'Listen kunne ikke gemmes.'))
    }
  }

  return (
    <Card
      title="Husets praktikmål"
      actions={
        <button className="btn btn--sm" onClick={() => setOpen((o) => !o)}>
          {open ? 'Luk' : 'Ret'}
        </button>
      }
    >
      <div className="tiny muted">
        Listen er et EKSEMPEL - ret den efter uddannelsesordningen. Målene kopieres ind, når en ny plan oprettes.
      </div>
      {open &&
        (isLoading ? (
          <Loading />
        ) : (
          <div className="stack" style={{ gap: 6, marginTop: 8 }}>
            {rows.map((r) => (
              <div key={r.key} className="row" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <input value={r.area} onChange={(e) => change(r.key, { area: e.target.value })} placeholder="Område" maxLength={100} style={{ width: 150 }} />
                <input value={r.text} onChange={(e) => change(r.key, { text: e.target.value })} maxLength={500} style={{ flex: '1 1 220px' }} />
                <label className="tiny row" style={{ gap: 4 }}>
                  <input type="checkbox" style={{ width: 'auto' }} checked={r.isActive} onChange={(e) => change(r.key, { isActive: e.target.checked })} />
                  Med
                </label>
                <button className="btn btn--sm" aria-label="Fjern målet" onClick={() => setDraft(rows.filter((x) => x.key !== r.key))}>
                  ✕
                </button>
              </div>
            ))}
            <div>
              <button className="btn btn--sm" onClick={() => setDraft([...rows, { key: `n${Date.now()}`, area: rows.at(-1)?.area ?? '', text: '', isActive: true }])}>
                + Praktikmål
              </button>
            </div>
            {error && <div className="error-box">{error}</div>}
            <div>
              <button className="btn btn--primary" disabled={!draft || action.isPending} onClick={() => void save()}>
                Gem listen
              </button>
            </div>
          </div>
        ))}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Én plan
// ---------------------------------------------------------------------------

export function ApprenticePage() {
  const id = Number(useParams().id)
  const { data: plan, isLoading, error } = useApprenticeship(id)
  const action = useApprenticeAction()
  const [editing, setEditing] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!plan) return <EmptyState>Planen findes ikke, eller den er ikke din.</EmptyState>

  const approved = plan.goals.filter((g) => g.approvedAt).length
  const areas = [...new Set(plan.goals.map((g) => g.area))]
  const today = todayIso()

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          🎓 {plan.apprentice.displayName}
        </h1>
        <Link className="btn btn--sm" to="/intranet?fane=apprentices">
          Alle lærlinge
        </Link>
      </div>

      {editing ? (
        <PlanForm plan={plan} onClose={() => setEditing(false)} />
      ) : (
        <Card>
          <div className="stack" style={{ gap: 4 }}>
            <div className="small">
              <strong>{plan.education}</strong> · startet {formatDate(plan.startDate)}
              {plan.expectedEndDate ? ` · svendeprøve ${formatDate(plan.expectedEndDate)}` : ''}
            </div>
            <div className="tiny muted">
              Ansvarlig svend: {plan.journeyman?.displayName ?? 'ikke sat - så er det kontoret'} · {approved} af {plan.goals.length} praktikmål
              {plan.completedAt ? ` · afsluttet ${formatDate(plan.completedAt)}` : ''}
            </div>
            {plan.notes && <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{plan.notes}</div>}
            {plan.canManage && (
              <div className="row" style={{ gap: 8, marginTop: 8 }}>
                <button className="btn btn--sm" onClick={() => setEditing(true)}>
                  Ret
                </button>
                <button
                  className="btn btn--sm"
                  onClick={async () => {
                    if (!window.confirm('Afslut læretiden? Planen bliver stående som historik og kan ikke rettes.')) return
                    setActionError(null)
                    try {
                      await action.mutateAsync({ path: `/${plan.id}/complete` })
                    } catch (err) {
                      setActionError(errorText(err, 'Det lykkedes ikke.'))
                    }
                  }}
                >
                  Afslut læretiden
                </button>
              </div>
            )}
          </div>
        </Card>
      )}
      {actionError && <div className="error-box">{actionError}</div>}

      <Card title="Skoleperioder">
        {plan.schoolPeriods.length === 0 ? (
          <div className="tiny muted">Ingen skoleperioder. Kontoret skriver dem som fravær "Skole" i kalenderen.</div>
        ) : (
          <div className="stack" style={{ gap: 4 }}>
            {plan.schoolPeriods.map((s) => (
              <div key={s.absenceId} className={`small ${s.endDate < today ? 'muted' : ''}`}>
                🏫 {formatDate(s.startDate)} - {formatDate(s.endDate)}
                {s.note ? ` · ${s.note}` : ''}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title={`Praktikmål (${approved}/${plan.goals.length})`}>
        <div className="stack" style={{ gap: 10 }}>
          {!plan.canSupervise && plan.isApprentice && (
            <div className="tiny muted">Din svend krydser målene af, når du har vist, at du kan det.</div>
          )}
          {areas.map((area) => (
            <div key={area} className="stack" style={{ gap: 4 }}>
              <div className="small">
                <strong>{area}</strong>
              </div>
              {plan.goals.filter((g) => g.area === area).map((g) => (
                <GoalRow key={g.id} plan={plan} goal={g} />
              ))}
            </div>
          ))}
          {plan.canManage && <AddGoal planId={plan.id} />}
        </div>
      </Card>

      <Card title="Logbog">
        <div className="stack" style={{ gap: 12 }}>
          {plan.isApprentice && <LogForm plan={plan} />}
          {plan.log.length === 0 && <EmptyState>Ingen indlæg endnu.</EmptyState>}
          {plan.log.map((e) => (
            <LogEntry key={e.id} plan={plan} entry={e} />
          ))}
        </div>
      </Card>
    </div>
  )
}

function GoalRow({ plan, goal }: { plan: Apprenticeship; goal: ApprenticeGoal }) {
  const action = useApprenticeAction()
  const [error, setError] = useState<string | null>(null)

  async function toggle(approved: boolean) {
    setError(null)
    const comment = approved ? window.prompt('Kommentar til lærlingen (valgfri):', goal.comment ?? '') : null
    if (approved && comment === null) return
    try {
      await action.mutateAsync({ path: `/${plan.id}/goals/${goal.id}`, method: 'put', body: { approved, comment: comment || null } })
    } catch (err) {
      setError(errorText(err, 'Det lykkedes ikke.'))
    }
  }

  return (
    <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
      <input
        type="checkbox"
        style={{ width: 'auto', marginTop: 3 }}
        checked={!!goal.approvedAt}
        disabled={!plan.canSupervise || action.isPending}
        onChange={(e) => void toggle(e.target.checked)}
        aria-label={goal.text}
      />
      <div style={{ minWidth: 0 }}>
        <div className="small">{goal.text}</div>
        {goal.approvedAt && (
          <div className="tiny muted">
            ✓ {goal.approvedBy?.displayName ?? '-'} {formatDate(goal.approvedAt)}
            {goal.comment ? ` · ${goal.comment}` : ''}
          </div>
        )}
        {error && <div className="tiny warn">{error}</div>}
      </div>
    </div>
  )
}

function AddGoal({ planId }: { planId: number }) {
  const action = useApprenticeAction()
  const [text, setText] = useState('')
  const [area, setArea] = useState('')

  return (
    <form
      className="row"
      style={{ gap: 6, flexWrap: 'wrap' }}
      onSubmit={async (e) => {
        e.preventDefault()
        if (!text.trim()) return
        await action.mutateAsync({ path: `/${planId}/goals`, body: { area: area.trim() || null, text: text.trim() } })
        setText('')
      }}
    >
      <input value={area} onChange={(e) => setArea(e.target.value)} placeholder="Område" style={{ width: 140 }} maxLength={100} />
      <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Ekstra praktikmål til netop denne lærling" style={{ flex: '1 1 220px' }} maxLength={500} />
      <button className="btn btn--sm" disabled={!text.trim()}>
        + Tilføj
      </button>
    </form>
  )
}

function LogForm({ plan }: { plan: Apprenticeship }) {
  const action = useApprenticeAction()
  const { data: projects } = useProjects({ pageSize: 200 })
  const [date, setDate] = useState(todayIso())
  const [text, setText] = useState('')
  const [projectId, setProjectId] = useState('')
  const [goalId, setGoalId] = useState('')
  const [photos, setPhotos] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    const form = new FormData()
    form.set('date', date)
    form.set('text', text.trim())
    if (projectId) form.set('projectId', projectId)
    if (goalId) form.set('goalId', goalId)
    photos.forEach((p) => form.append('photos', p))
    try {
      await action.mutateAsync({ path: `/${plan.id}/log`, form })
      setText('')
      setProjectId('')
      setGoalId('')
      setPhotos([])
    } catch (err) {
      setError(errorText(err, 'Indlægget kunne ikke gemmes.'))
    }
  }

  return (
    <form className="stack" style={{ gap: 8 }} onSubmit={(e) => void submit(e)}>
      <div className="small">
        <strong>Skriv i logbogen</strong>
      </div>
      <input type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} style={{ width: 'auto' }} />
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Hvad lavede du i dag?" maxLength={4000} />
      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} style={{ flex: '1 1 180px' }} aria-label="Sag">
          <option value="">Ingen sag</option>
          {(projects?.items ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select value={goalId} onChange={(e) => setGoalId(e.target.value)} style={{ flex: '1 1 180px' }} aria-label="Praktikmål">
          <option value="">Intet praktikmål</option>
          {plan.goals.map((g) => (
            <option key={g.id} value={g.id}>
              {g.text}
            </option>
          ))}
        </select>
      </div>
      <label className="small">
        Billeder (højst 3)
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => setPhotos(Array.from(e.target.files ?? []).slice(0, 3))}
        />
      </label>
      {error && <div className="error-box">{error}</div>}
      <div>
        <button className="btn btn--primary" disabled={!text.trim() || action.isPending}>
          {action.isPending ? 'Gemmer ...' : 'Gem indlægget'}
        </button>
      </div>
    </form>
  )
}

function LogEntry({ plan, entry }: { plan: Apprenticeship; entry: Apprenticeship['log'][number] }) {
  const action = useApprenticeAction()
  const [comment, setComment] = useState(entry.comment ?? '')
  const [commenting, setCommenting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function run(path: string, method: 'put' | 'delete', body?: unknown) {
    setError(null)
    try {
      await action.mutateAsync({ path: `/${plan.id}/log/${entry.id}${path}`, method, body })
      setCommenting(false)
    } catch (err) {
      setError(errorText(err, 'Det lykkedes ikke.'))
    }
  }

  return (
    <div className="stack" style={{ gap: 6, paddingTop: 8, borderTop: '1px solid var(--border, #E2E8F0)' }}>
      <div className="tiny muted">
        {formatDate(entry.date)}
        {entry.projectName ? ` · ${entry.projectName}` : ''}
        {entry.goalText ? ` · 🎯 ${entry.goalText}` : ''}
      </div>
      <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{entry.text}</div>
      {entry.photos.length > 0 && (
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {entry.photos.map((p) => (
            <IntranetPhotoThumb key={p.id} photo={p} />
          ))}
        </div>
      )}
      {entry.comment && !commenting && (
        <div className="small" style={{ background: 'rgba(59,130,246,0.08)', padding: 8, borderRadius: 8 }}>
          💬 {entry.commentedBy?.displayName}: {entry.comment}
          {entry.commentedAt && <span className="tiny muted"> · {formatDateTime(entry.commentedAt)}</span>}
        </div>
      )}
      {commenting && (
        <div className="stack" style={{ gap: 6 }}>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} maxLength={2000} />
          <div className="row" style={{ gap: 6 }}>
            <button className="btn btn--sm btn--primary" onClick={() => void run('/comment', 'put', { comment: comment.trim() || null })}>
              Gem kommentaren
            </button>
            <button className="btn btn--sm" onClick={() => setCommenting(false)}>
              Fortryd
            </button>
          </div>
        </div>
      )}
      <div className="row" style={{ gap: 6 }}>
        {plan.canSupervise && !commenting && (
          <button className="btn btn--sm" onClick={() => setCommenting(true)}>
            {entry.comment ? 'Ret kommentaren' : 'Kommentér'}
          </button>
        )}
        {plan.isApprentice && (
          <button
            className="btn btn--sm"
            onClick={() => {
              if (window.confirm('Slet indlægget?')) void run('', 'delete')
            }}
          >
            Slet
          </button>
        )}
      </div>
      {error && <div className="tiny warn">{error}</div>}
    </div>
  )
}
